import React from "react";
import KeyboardArrowLeft from "@components/icons/KeyboardArrowLeft";
import KeyboardArrowRight from "@components/icons/KeyboardArrowRight";
import KeyboardArrowUp from "@components/icons/KeyboardArrowUp";
import KeyboardArrowDown from "@components/icons/KeyboardArrowDown";
import cn from "./helpers/cn";

export type TabsOrientation = "horizontal" | "vertical";
export type TabsVariant = "standard" | "scrollable" | "fullWidth";
export type TabsIndicatorColor = "primary" | "secondary";
export type TabsTextColor = "inherit" | "primary" | "secondary";
export type TabsScrollButtons = "auto" | "desktop" | "on" | "off";

export interface TabsContextValue {
  value: unknown;
  onChange: (event: React.SyntheticEvent, value: unknown) => void;
  indicatorColor: TabsIndicatorColor;
  textColor: TabsTextColor;
  orientation: TabsOrientation;
  variant: TabsVariant;
}

export const TabsContext = React.createContext<TabsContextValue | null>(null);

export interface TabsProps<T = unknown> extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  value: T;
  onChange?: (event: React.SyntheticEvent, value: T) => void;
  orientation?: TabsOrientation;
  variant?: TabsVariant;
  indicatorColor?: TabsIndicatorColor;
  textColor?: TabsTextColor;
  centered?: boolean;
  scrollButtons?: TabsScrollButtons;
  scrollButtonsColor?: React.CSSProperties["color"];
  TabIndicatorProps?: React.HTMLAttributes<HTMLSpanElement>;
  ScrollButtonComponent?: React.ElementType;
  component?: React.ElementType;
  children?: React.ReactNode;
}

// Wrapper de tipo para preservar o generic através de React.forwardRef.
type TabsComponent = <T = unknown>(
  props: TabsProps<T> & { ref?: React.Ref<HTMLDivElement>; }
) => React.ReactElement | null;

const indicatorColorMap: Record<TabsIndicatorColor, string> = {
  primary: "bg-primary",
  secondary: "bg-secondary",
};

// Componente padrão de botão de scroll (segue MUI v4: usa TabScrollButton interno).
const DefaultScrollButton: React.FC<{
  direction: "left" | "right" | "up" | "down";
  disabled: boolean;
  onClick: () => void;
  orientation: TabsOrientation;
  visible: boolean;
  scrollButtonsColor?: TabsProps["scrollButtonsColor"]
}> = ({ direction, disabled, onClick, orientation, visible, scrollButtonsColor }) => {
  const Icon = direction === "left" ? KeyboardArrowLeft
    : direction === "right" ? KeyboardArrowRight
      : direction === "up" ? KeyboardArrowUp
        : KeyboardArrowDown;

  return (
    <button
      type="button"
      tabIndex={-1}
      onClick={onClick}
      aria-hidden="true"
      aria-disabled={disabled || undefined}
      className={cn(
        "shrink-0 inline-flex items-center justify-center text-slate-600",
        orientation === "horizontal" ? "w-10 self-stretch" : "h-10 self-stretch",
        // `auto` esconde os botões em dispositivos sem hover (touch). Usuários
        // podem forçar com `on`/`off`; ver lógica em Tabs.
        !visible && "opacity-0 pointer-events-none w-0 h-0 overflow-hidden",
        disabled && "opacity-30 cursor-default",
        !disabled && visible && "hover:bg-black/[0.04]"
      )}
    >
      <Icon style={{ color: scrollButtonsColor }} />
    </button>
  );
};

const Tabs = React.forwardRef(function Tabs<T = unknown>(
  props: TabsProps<T>,
  ref: React.Ref<HTMLDivElement>
) {
  const {
    className,
    component: Component = "div",
    value,
    onChange,
    orientation = "horizontal",
    variant = "standard",
    indicatorColor = "secondary",
    textColor = "inherit",
    centered = false,
    scrollButtons = "auto",
    TabIndicatorProps,
    ScrollButtonComponent,
    children,
    scrollButtonsColor,
    ...other
  } = props;

  const scrollerRef = React.useRef<HTMLDivElement | null>(null);
  const tabListRef = React.useRef<HTMLDivElement | null>(null);
  const [indicator, setIndicator] = React.useState<{ left: number; top: number; width: number; height: number; }>({ left: 0, top: 0, width: 0, height: 0 });
  const [scrollState, setScrollState] = React.useState({ start: false, end: false });

  const isHorizontal = orientation === "horizontal";
  const isScrollable = variant === "scrollable";

  // O TabsContext mantém value como `unknown` para não exigir T no Tab; aqui
  // refazemos o cast para T quando reemitimos ao onChange do consumidor.
  const handleChange = React.useCallback((event: React.SyntheticEvent, v: unknown) => {
    onChange?.(event, v as T);
  }, [onChange]);

  // Mede a posição do tab ativo e atualiza o indicador.
  const updateIndicator = React.useCallback(() => {
    const list = tabListRef.current;

    if (!list) return;
    const active = list.querySelector<HTMLElement>("[data-tab-selected=\"true\"]");

    if (!active) {
      setIndicator((prev) =>
        prev.left === 0 && prev.top === 0 && prev.width === 0 && prev.height === 0
          ? prev
          : { left: 0, top: 0, width: 0, height: 0 }
      );

      return;
    }
    const listRect = list.getBoundingClientRect();
    const rect = active.getBoundingClientRect();
    const next = {
      left: rect.left - listRect.left + list.scrollLeft,
      top: rect.top - listRect.top + list.scrollTop,
      width: rect.width,
      height: rect.height,
    };

    setIndicator((prev) =>
      prev.left === next.left && prev.top === next.top && prev.width === next.width && prev.height === next.height
        ? prev
        : next
    );
  }, []);

  // Verifica se ainda há conteúdo rolável em cada direção (para habilitar/desabilitar botões).
  const updateScrollButtons = React.useCallback(() => {
    const scroller = scrollerRef.current;

    if (!scroller || !isScrollable) {
      setScrollState((prev) => (!prev.start && !prev.end ? prev : { start: false, end: false }));

      return;
    }

    const showStart = isHorizontal
      ? scroller.scrollLeft > 1
      : scroller.scrollTop > 1;
    const showEnd = isHorizontal
      ? scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1
      : scroller.scrollTop + scroller.clientHeight < scroller.scrollHeight - 1;

    setScrollState((prev) =>
      prev.start === showStart && prev.end === showEnd ? prev : { start: showStart, end: showEnd }
    );
  }, [isHorizontal, isScrollable]);

  React.useLayoutEffect(() => {
    updateIndicator();
    updateScrollButtons();
  });

  React.useEffect(() => {
    const handler = () => {
      updateIndicator();
      updateScrollButtons();
    };

    window.addEventListener("resize", handler);

    return () => window.removeEventListener("resize", handler);
  }, [updateIndicator, updateScrollButtons]);

  // Em scrollable, mantém o tab selecionado visível (igual ao MUI).
  React.useEffect(() => {
    if (!isScrollable) return;
    const list = tabListRef.current;
    const scroller = scrollerRef.current;

    if (!list || !scroller) return;
    const active = list.querySelector<HTMLElement>("[data-tab-selected=\"true\"]");

    if (!active) return;

    if (isHorizontal) {
      const left = active.offsetLeft;
      const right = left + active.offsetWidth;
      const viewStart = scroller.scrollLeft;
      const viewEnd = viewStart + scroller.clientWidth;

      if (left < viewStart) scroller.scrollTo({ left, behavior: "smooth" });
      else if (right > viewEnd) scroller.scrollTo({ left: right - scroller.clientWidth, behavior: "smooth" });
    } else {
      const top = active.offsetTop;
      const bottom = top + active.offsetHeight;
      const viewStart = scroller.scrollTop;
      const viewEnd = viewStart + scroller.clientHeight;

      if (top < viewStart) scroller.scrollTo({ top, behavior: "smooth" });
      else if (bottom > viewEnd) scroller.scrollTo({ top: bottom - scroller.clientHeight, behavior: "smooth" });
    }
  }, [value, isScrollable, isHorizontal]);

  const moveScroll = (delta: number) => {
    const scroller = scrollerRef.current;

    if (!scroller) return;

    if (isHorizontal) {
      scroller.scrollBy({ left: delta, behavior: "smooth" });
    } else {
      scroller.scrollBy({ top: delta, behavior: "smooth" });
    }
  };

  const handleScrollerScroll = () => updateScrollButtons();

  const ctx = React.useMemo<TabsContextValue>(() => ({
    value,
    onChange: handleChange,
    indicatorColor,
    textColor,
    orientation,
    variant,
  }), [value, handleChange, indicatorColor, textColor, orientation, variant]);

  // Injeta o index nos filhos para fallback de value e selected.
  const childrenWithProps = React.Children.map(children, (child, index) => {
    if (!React.isValidElement(child)) return child;

    return React.cloneElement(child as React.ReactElement<{ "data-tab-index"?: number; }>, { "data-tab-index": index });
  });

  // Decide a visibilidade dos botões de scroll.
  // off       -> sem botões
  // on        -> sempre visíveis
  // desktop   -> só em dispositivos com hover (CSS @media via hidden em touch — aqui sempre exibe se houver overflow)
  // auto      -> exibe se houver overflow (default MUI v4)
  // Em telas touch (sem hover), a regra CSS abaixo esconde para auto/desktop.
  const showButtons = isScrollable && scrollButtons !== "off";
  const ButtonComponent = (ScrollButtonComponent as typeof DefaultScrollButton | undefined) ?? DefaultScrollButton;

  // Em scrollButtons="on", botões ficam sempre visíveis (mesmo desabilitados).
  // Em "auto"/"desktop", a visibilidade segue scrollState; desktop adiciona
  // restrição CSS para esconder em telas sem hover.
  const startVisible = scrollButtons === "on" ? true : scrollState.start;
  const endVisible = scrollButtons === "on" ? true : scrollState.end;

  const hideOnTouch = scrollButtons === "auto" || scrollButtons === "desktop";

  return (
    <TabsContext.Provider value={ctx}>
      <Component
        ref={ref}
        role="tablist"
        aria-orientation={orientation}
        className={cn(
          "relative min-h-[48px] overflow-hidden",
          isHorizontal ? "flex flex-row items-center" : "flex flex-col",
          className
        )}
        {...other}
      >
        {showButtons && (
          <div className={cn(hideOnTouch && "[@media(hover:none)]:hidden flex")}>
            <ButtonComponent
              direction={isHorizontal ? "left" : "up"}
              disabled={!scrollState.start}
              visible={startVisible}
              orientation={orientation}
              scrollButtonsColor={scrollButtonsColor}
              onClick={() => moveScroll(isHorizontal ? -(scrollerRef.current?.clientWidth ?? 0) : -(scrollerRef.current?.clientHeight ?? 0))}
            />
          </div>
        )}

        <div
          ref={scrollerRef}
          onScroll={handleScrollerScroll}
          className={cn(
            // Em scrollable permite overflow; nos demais comporta dentro do container.
            "relative inline-block flex-1",
            isScrollable && (isHorizontal ? "overflow-x-auto overflow-y-hidden" : "overflow-y-auto overflow-x-hidden"),
            // Esconde scrollbar nativa em scrollable (mantém a navegação pelos botões).
            isScrollable && "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
            !isScrollable && "w-full"
          )}
        >
          <div
            ref={tabListRef}
            className={cn(
              "relative",
              isHorizontal ? "flex flex-row" : "flex flex-col",
              isHorizontal && variant === "fullWidth" && "w-full",
              isHorizontal && centered && !isScrollable && "justify-center"
            )}
          >
            {childrenWithProps}

            {/* Indicador animado posicionado abaixo (horizontal) ou à direita (vertical) do tab ativo. */}
            <span
              aria-hidden="true"
              {...TabIndicatorProps}
              className={cn(
                "absolute transition-all duration-300 ease-out",
                indicatorColorMap[indicatorColor],
                isHorizontal ? "bottom-0 h-0.5" : "right-0 w-0.5",
                TabIndicatorProps?.className
              )}
              style={{
                ...(isHorizontal
                  ? { left: indicator.left, width: indicator.width }
                  : { top: indicator.top, height: indicator.height }),
                ...TabIndicatorProps?.style,
              }}
            />
          </div>
        </div>

        {showButtons && (
          <div className={cn(hideOnTouch && "[@media(hover:none)]:hidden flex")}>
            <ButtonComponent
              direction={isHorizontal ? "right" : "down"}
              disabled={!scrollState.end}
              visible={endVisible}
              orientation={orientation}
              onClick={() => moveScroll(isHorizontal ? (scrollerRef.current?.clientWidth ?? 0) : (scrollerRef.current?.clientHeight ?? 0))}
            />
          </div>
        )}
      </Component>
    </TabsContext.Provider>
  );
}) as TabsComponent & { displayName?: string; };

Tabs.displayName = "Tabs";

export default Tabs;
